<script setup>
import { watch, ref, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useMediaQuery } from '@vueuse/core';
import HeaderLogo from '@/components/layout/Header/HeaderLogo.vue';
import Search from '@/components/layout/Header/Search.vue';
import LanguageSwitcher from '@/components/layout/Header/LanguageSwitcher.vue';
import WorkTime from '@/components/layout/Header/WorkTime.vue';
import Button from '@/components/layout/Header/Button.vue';
import UkravtoGroup from '@/components/layout/Header/UkravtoGroup.vue';
import Nav from '@/components/layout/Header/Nav.vue';

import MobileMenu from '@/components/layout/Header/MobileMenu.vue';
import { useUiStore } from '@/stores/ui';

const ui = useUiStore();

defineProps({
  data: {
    type: Object,
    default: () => {},
  },
  socials: {
    type: Array,
    default: () => [],
  },
});
const isDesktopMedia = useMediaQuery('(min-width: 1024px)');
const isTablet = useMediaQuery('(min-width: 768px)');
const renderDesktopElements = computed(() => {
  if (process.server) return true;
  
  return isDesktopMedia.value;
});

const isOpen = ref(false);
const toggleBurger = () => {
  isOpen.value = !isOpen.value;
};

const route = useRoute();

const router = useRouter();
const isMobileMenuOpen = ref(false);

const openMobileMenu = () => {
  isMobileMenuOpen.value = true;
  document.body.style.overflow = 'hidden';
};

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false;
  document.body.style.overflow = '';
};

const handleNavigation = (route) => {
  router.push(route);
  closeMobileMenu();
};

watch(isDesktopMedia, (newValue) => {
  if (newValue) {
    closeMobileMenu();
  }
});

const closeMenus = () => {
  isOpen.value = false;
  closeMobileMenu();
};

watch(
  [isDesktopMedia, isTablet],
  ([isDesktop, isTab], [wasDesktop, wasTab]) => {
    if (isDesktop !== wasDesktop && isTab && wasTab) {
      closeMenus();
    }
  },
);

watch(
  () => route.fullPath,
  () => {
    document.body.style.overflow = '';
    isOpen.value = false;
    isMobileMenuOpen.value = false;
  },
);
</script>

<template>
  <header :class="['header', ui.headerClass, isOpen ? 'header--open' : '']">
    <div class="container header__wrap">
      <div class="header__top">
        <div class="header__top-left">
          <div v-if="renderDesktopElements" class="header__burger" @click="toggleBurger">
            <span class="header__burger-line header__burger-line--1"></span>
            <span class="header__burger-line header__burger-line--2"></span>
            <span class="header__burger-line header__burger-line--3"></span>
            <span class="header__burger-line header__burger-line--4"></span>
          </div>
          <div v-else class="header__burger" @click="openMobileMenu">
            <span class="header__burger-line header__burger-line--1"></span>
            <span class="header__burger-line header__burger-line--2"></span>
            <span class="header__burger-line header__burger-line--3"></span>
            <span class="header__burger-line header__burger-line--4"></span>
          </div>
          <div
            v-if="
              data?.top_nav &&
              data?.top_nav?.length > 0 &&
              (renderDesktopElements || isTablet)
            "
            :class="['header__corp', { 'header__corp--hidden': isOpen }]"
          >
            <template v-for="link in data?.top_nav" :key="link.url">
              <CTagDetect
                v-if="link.url && link.url.length > 0"
                :to="link.url"
                class="header__corp-link link-small"
              >
                {{ link.title }}
              </CTagDetect>
            </template>
          </div>
        </div>
        <HeaderLogo
          :logo="isOpen ? data?.logo_white : data?.logo"
          class="header__logo"
        />
        <div class="header__top-right">
          <Search
            class="header__search"
            :class="{ 'header__search--white': isOpen }"
          />
          <!--
            <LanguageSwitcher
              class="header__langs"
              :class="{ 'header__langs--white': isOpen }"
            />
          -->
          <WorkTime
            v-if="data?.time && data?.time?.length > 0"
            :data="data?.time"
          />
          <Button
            v-if="data?.button && data?.button?.type"
            :data="data?.button"
          />
        </div>
      </div>
      <Nav
        v-if="data?.nav && data?.nav.length > 0 && renderDesktopElements"
        :nav="data?.nav"
      />
    </div>
    <MobileMenu
      :nav="data?.nav"
      :link="data?.link"
      :contacts="data?.contacts"
      :socials="socials"
      :ukravto-group="data?.ukravto_group"
      :is-open="isMobileMenuOpen"
      @close="closeMobileMenu"
      @navigate="handleNavigation"
    />
    <Transition name="dropdown">
      <template v-if="isOpen">
        <div class="header__dropdown bg-black white">
          <UkravtoGroup v-if="renderDesktopElements" :ukravto-group="data?.ukravto_group" />
        </div>
      </template>
    </Transition>
    <div
      v-if="renderDesktopElements && isOpen"
      class="header__overlay"
      @click="toggleBurger"
    ></div>
  </header>
</template>
<style scoped lang="scss">
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.3s linear;
  transform-origin: top;
}

.dropdown-enter-from {
  opacity: 0;
  transform: scaleY(0.95) translateY(-10px);
}

.dropdown-leave-to {
  opacity: 0;
  transform: scaleY(0.95) translateY(-10px);
}
.header {
  $parent: &;
  position: sticky;
  top: 0;
  z-index: 25;
  &__overlay {
    background-color: rgba($black, 0.7);
    bottom: 0;
    left: 0;
    position: fixed;
    right: 0;
    top: 0;
    z-index: 1;
  }
  &--open {
    background-color: $black;
    .header__burger {
      &-line {
        background-color: $white;
        &--1 {
          top: 18px;
          width: 0%;
          left: 50%;
          @include media-breakpoint-up(xl) {
            @include responsive-spacing(top, 18);
          }
        }
        &--2 {
          -webkit-transform: rotate(45deg);
          -moz-transform: rotate(45deg);
          -o-transform: rotate(45deg);
          transform: rotate(45deg);
        }
        &--3 {
          -webkit-transform: rotate(-45deg);
          -moz-transform: rotate(-45deg);
          -o-transform: rotate(-45deg);
          transform: rotate(-45deg);
        }
        &--4 {
          left: 50%;
          top: 18px;
          width: 0%;
          @include media-breakpoint-up(xl) {
            @include responsive-spacing(top, 18);
          }
        }
      }
    }
  }
  &__wrap {
    position: relative;
    z-index: 3;
  }
  &__top {
    align-items: center;
    display: grid;
    grid-template-columns: 4fr auto 4fr;
    height: 48px;
    @include media-breakpoint-up(sm) {
      grid-template-columns: 4fr auto 4fr;
      height: 88px;
      padding: 12px 0;
    }
    @include media-breakpoint-up(md) {
      grid-template-columns: 4fr auto 4fr;
      height: 96px;
      padding: 16px 0;
      position: relative;
      z-index: 2;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(height, 96);
      @include responsive-spacing(padding-top, 16);
      @include responsive-spacing(padding-bottom, 16);
    }
    &-left {
      align-items: center;
      display: grid;
      grid-template-columns: 32px auto;
      gap: 8px;
      margin-left: -8px;
      width: calc(100% + 8px);
      @include media-breakpoint-up(xs) {
        gap: 16px;
      }
      @include media-breakpoint-up(sm) {
        gap: 24px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 24);
        grid-template-columns: px-to-vw(32) auto;
        @include responsive-spacing(margin-left, -8);
        width: calc(100% + px-to-vw(8));
      }
    }
    &-right {
      align-items: center;
      display: flex;
      flex-direction: row;
      gap: 16px;
      justify-content: end;
      @include media-breakpoint-up(sm) {
        gap: 24px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 24);
      }
    }
  }
  &__burger {
    $parentburger: &;
    cursor: pointer;
    height: 32px;
    position: relative;
    -webkit-transform: rotate(0deg);
    -moz-transform: rotate(0deg);
    -o-transform: rotate(0deg);
    transform: rotate(0deg);
    width: 32px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(height, 32);
      @include responsive-spacing(width, 32);
    }

    &-line {
      background-color: $on-surface;
      display: block;
      height: 2px;
      left: 0;
      margin: 0 auto;
      opacity: 1;
      position: absolute;
      right: 0;
      -webkit-transform: rotate(0deg);
      -moz-transform: rotate(0deg);
      -o-transform: rotate(0deg);
      transform: rotate(0deg);
      -webkit-transition: 0.25s ease-in-out;
      -moz-transition: 0.25s ease-in-out;
      -o-transition: 0.25s ease-in-out;
      transition: 0.25s ease-in-out;
      width: 50%;

      &--1 {
        top: 8px;
      }
      &--2 {
        top: 15px;
      }
      &--3 {
        top: 15px;
      }
      &--4 {
        top: 22px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(height, 2);
        &--1 {
          @include responsive-spacing(top, 8);
        }
        &--2 {
          @include responsive-spacing(top, 15);
        }
        &--3 {
          @include responsive-spacing(top, 15);
        }
        &--4 {
          @include responsive-spacing(top, 22);
        }
      }
    }
  }
  &__corp {
    display: none;
    @include media-breakpoint-up(md) {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 8px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 8);
    }
    &--hidden {
      visibility: hidden;
    }
    &-link {
      color: $on-surface-variant-2;
      text-decoration: underline;
      @include media-breakpoint-up(md) {
        &:hover {
          text-decoration: unset;
        }
      }
    }
  }
  &__search {
    &--white {
      &:deep(.search__icon) {
        circle {
          stroke: $white;
        }
        path {
          fill: $white;
        }
      }
    }
  }
  &__langs {
    &--white {
      &:deep(.language-switcher__text) {
        color: $white;
      }
      &:deep(.language-switcher__icon) {
        path {
          stroke: $white;
        }
      }
    }
  }
  &__dropdown {
    left: 0;
    padding: 48px 48px 24px;
    position: absolute;
    top: 0px;
    width: 100%;
    z-index: 20;
    @include media-breakpoint-up(sm) {
      top: 88px;
    }
    @include media-breakpoint-up(md) {
      padding-top: 24px;
      top: 96px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(padding-top, 24);
      @include responsive-spacing(padding-bottom, 24);
      @include responsive-spacing(padding-left, 48);
      @include responsive-spacing(padding-right, 48);
      @include responsive-spacing(top, 96);
    }
  }
}
</style>
